import { CdkPortalOutlet, PortalModule } from '@angular/cdk/portal';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { SharedModule } from 'primeng/api';
import { DialogModule } from 'primeng/dialog';
import { DialogService } from './fcp-dialog.service';

@Component({
    selector: 'fcp-dialog',
    imports: [CommonModule, DialogModule, PortalModule, CdkPortalOutlet, SharedModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '[attr.data-e2e-hook]': 'e2e()'
    },
    template: `
        <p-dialog
            [visible]="dialogService.dialogVisible()"
            (visibleChange)="handleVisibleChange($event)"
            [header]="headless() || headerPortal() ? '' : dialogTitle()"
            [style]="dialogStyle()"
            [styleClass]="dialogStyleClass()"
            [showHeader]="true"
            [maximizable]="dialogMaximizable()"
            [focusTrap]="true"
            [draggable]="false"
            [resizable]="false"
            [closeOnEscape]="true"
            [closable]="true"
            [autoZIndex]="true"
            [closeIcon]="closeIcon"
            [modal]="dialogBackdrop()"
            [maximizeIcon]="maximizeIcon"
            [minimizeIcon]="minimizeIcon"
        >
            @if (headless()) {
                <ng-template #headless>
                    @if (headlessContent(); as headlessContent) {
                        <ng-container *cdkPortalOutlet="headlessContent"></ng-container>
                    } @else {
                        <ng-content></ng-content>
                    }
                </ng-template>
            } @else {
                <ng-template #header>
                    @if (headerPortal(); as headerPortal) {
                        <ng-container *cdkPortalOutlet="headerPortal"></ng-container>
                    } @else {
                        <span class="p-dialog-title">{{ dialogTitle() }}</span>
                    }
                </ng-template>

                <ng-template #content>
                    @if (contentPortal(); as contentPortal) {
                        <ng-container *cdkPortalOutlet="contentPortal"></ng-container>
                    } @else {
                        <ng-content></ng-content>
                    }
                </ng-template>

                <ng-template #footer>
                    @if (footerPortal(); as footerPortal) {
                        <ng-container *cdkPortalOutlet="footerPortal"></ng-container>
                    } @else {
                        <ng-content></ng-content>
                    }
                </ng-template>
            }
        </p-dialog>
    `
})
export class FcpDialogComponent {
    dialogService = inject(DialogService);

    e2e = this.dialogService.e2eHook;
    dialogWidth = this.dialogService.dialogWidth;
    dialogSize = this.dialogService.dialogSize;
    dialogBackdrop = this.dialogService.backdrop;

    readonly dialogStyle = computed(() => {
        const width = this.dialogWidth();
        return width ? { width } : null;
    });

    readonly dialogStyleClass = computed(() => {
        if (this.dialogWidth()) {
            return '';
        }
        return `fcp-dialog-size-${this.dialogSize()}`;
    });
    dialogTitle = this.dialogService.dialogTitle;
    dialogMaximizable = this.dialogService.dialogMaximizable;

    /* Headless handlers */
    headless = this.dialogService.headless;
    headlessContent = this.dialogService.headlessContent;

    /* Templates handlers */
    headerPortal = this.dialogService.headerPortal;
    contentPortal = this.dialogService.contentPortal;
    footerPortal = this.dialogService.footerPortal;

    readonly closeIcon = 'fc-font-icon fc-font-icon-close';
    readonly maximizeIcon = 'fc-font-icon fc-font-icon-fullscreen';
    readonly minimizeIcon = 'fc-font-icon fc-font-icon-collapse';

    handleDialogHide(): void {
        this.dialogService.close();
    }

    handleVisibleChange(visible: boolean): void {
        if (!visible) {
            this.dialogService.close();
        }
    }
}
